import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MentionsInput, Mention, SuggestionDataItem, MentionItem } from 'react-mentions';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { Box, Tooltip } from '@mui/material';
import { useForm } from 'react-hook-form';
import cx from 'classnames';

import { CLIP, COMMENT, COMMENTDTO, COMMENT_PARENT_TYPE, GROUP, GROUP_WITH_SUBGROUP, NOTIFICATION, USER } from '../../../../enums';
import { updateComposerPlayControls } from '../../../../store/composers/composersSlice';
import { ReactComponent as ArrowUpIcon } from '../../../../assets/icons/send.svg';
import { updateVideoControls } from '../../../../store/videos/videoSlice';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { createNewComment, searchUsersAndGroups } from '../../../../api';
import { findSearchValue } from '../../../../helpers';
import styles from './CommentFooter.module.scss';
import LinearProgress from '../../LinearProgress';
import mentionsStyle from './style';
import Avatar from '../../Avatar';
import { enqueueSnackbar } from 'notistack';

const ALL_WITH_ACCESS_ID = 'All with access';
const NO_MENTION_TARGETS_ID = '__no_mention_targets__';
const NO_MENTION_TARGETS_LABEL = 'Not shared with anyone yet';
const NO_RESULTS_ID = '__no_results__';
const NO_RESULTS_LABEL = 'No results';

type TCommentFooter = {
  setImpertivlyShowFooter?: (bool: boolean) => void;
  fetch: (value: string) => void;
  setShowFooter?: (value: boolean) => void;
  videoPlayedSeconds?: number;
  selectedClipsList?: CLIP[];
  commentData?: COMMENT;
  videofeedId?: string;
  showFooter?: boolean;
  commentId?: string;
  disable?: boolean;
  reply?: boolean;
  small?: boolean;
  section?: COMMENT_PARENT_TYPE;
  selectedClipObject?: CLIP;
  clipId?: string;
  target?: { parentType: COMMENT_PARENT_TYPE.CLIP | COMMENT_PARENT_TYPE.VIDEOFEED; parentId: string } | null;
  placeholder?: string;
  onPosted?: (comment: COMMENT) => void;
};

const CommentFooter: React.FC<TCommentFooter> = ({
  setImpertivlyShowFooter,
  videoPlayedSeconds,
  selectedClipsList,
  setShowFooter,
  videofeedId,
  commentData,
  showFooter,
  commentId,
  disable,
  reply,
  fetch,
  small,
  section,
  selectedClipObject,
  clipId,
  target,
  placeholder,
  onPosted,
}) => {
  const { handleSubmit } = useForm<COMMENTDTO>({
    defaultValues: {
      message: '',
      parentId: '',
      videofeedId,
    },
  });
  const feedId = findSearchValue('feedId');

  const notification = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [markupValue, setMarkupValue] = useState('');
  const [value, setValue] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const inputDisabled = !!disable || target === null;
  const currentTarget = useRef({ target, disabled: inputDisabled });
  currentTarget.current = { target, disabled: inputDisabled };
  const mentionDebounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setMarkupValue('');
    setValue('');
    setTags([]);
  }, [feedId]);

  const { currentUser } = useAppSelector((state) => state.user);
  const { videoControls, currentPlayingTimelineClip, sortedSelectedClips } = useAppSelector((state) => state.videoStore);
  const { composerPlayControl, composerRows } = useAppSelector((state) => state.composersStore);

  const dispatch = useAppDispatch();

  const getUsersAndGroups = (query: string, callback: (data: SuggestionDataItem[]) => void) => {
    if (mentionDebounceTimerRef.current) {
      clearTimeout(mentionDebounceTimerRef.current);
    }

    mentionDebounceTimerRef.current = setTimeout(async () => {
      try {
        const res = await searchUsersAndGroups(videofeedId!, query);

        const userList = res.data.users.map((user: USER) => ({
          ...user,
          display: user.username,
          id: user.id,
        }));

        const groupList = res.data.groups.map((group: GROUP) => ({
          title: group.title,
          display: group.title,
          id: group.id,
        }));

        const subGroupList = res.data.subgroups.map((subGroupAndGroup: { group: GROUP; subgroup: GROUP }) => {
          return {
            ...subGroupAndGroup.subgroup,
            title: `${subGroupAndGroup.group.title} - ${subGroupAndGroup.subgroup.title}`,
            display: `${subGroupAndGroup.group.title} - ${subGroupAndGroup.subgroup.title}`,
            id: subGroupAndGroup.subgroup.id,
          };
        });

        const hasMentionTargets = userList.length + groupList.length + subGroupList.length > 0;
        if (!hasMentionTargets) {
          if (query.trim().length) {
            callback([{ display: NO_RESULTS_LABEL, id: NO_RESULTS_ID }]);
            return;
          }

          if (!query.trim().length) {
            enqueueSnackbar('Content is not shared with any other user', {
              variant: 'warning',
              autoHideDuration: 4000,
            });
          }
          callback([{ display: NO_MENTION_TARGETS_LABEL, id: NO_MENTION_TARGETS_ID }]);
          return;
        }

        callback([...userList, ...groupList, ...subGroupList, { display: ALL_WITH_ACCESS_ID, id: ALL_WITH_ACCESS_ID }]);
      } catch (error) {
        console.log(error);
        callback([{ display: NO_RESULTS_LABEL, id: NO_RESULTS_ID }]);
      }
    }, 250);
  };

  const style = {
    [styles.footer]: true,
    [styles.footerReply]: reply,
    [styles.disabled]: inputDisabled,
  };

  const handleInputChange = (
    event: { target: { value: string } },
    newValue: string,
    newPlainTextValue: string,
    mentions: MentionItem[],
  ) => {
    setMarkupValue(newValue);
    setValue(newPlainTextValue);
    setTags(
      Array.from(
        new Set(
          (mentions || [])
            .map((mention) => `${mention.id}`)
            .filter((id) => id !== NO_MENTION_TARGETS_ID && id !== NO_RESULTS_ID),
        ),
      ),
    );
  };

  const handleInputClick = () => {
    dispatch(updateVideoControls({ ...videoControls, playing: false }));
    dispatch(
      updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        playing: false,
      }),
    );
    focusOnInput();
  };

  const [groups, setGroups] = useState<GROUP_WITH_SUBGROUP[]>([]);
  const [users, setUsers] = useState<USER[]>([]);

  const fetchAll = async () => {
    try {
      const res = await searchUsersAndGroups(videofeedId!, '');
      const subgroups = res.data.subgroups.map((subgroupAndGroup: { group: GROUP; subgroup: GROUP }) => subgroupAndGroup.subgroup);

      setUsers(res.data.users);
      setGroups([...res.data.groups, ...subgroups]);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const isTimeline = useMemo(() => {
    return window.location.href.includes('/Timeline');
  }, [window.location.href]);

  const postNewComment = async () => {
    const { target: submitTarget, disabled } = currentTarget.current;
    if (loading || disabled) return;
    if (!value.trim().length) {
      notification.showMessage('Empty message');
      return;
    }
    try {
      setLoading(true);

      const comment = {} as COMMENTDTO;

      comment.message = value;
      comment.videofeedId = videofeedId as string;
      if (typeof videoPlayedSeconds === 'number') {
        comment.timestamp = videoPlayedSeconds;
      }

      if (value?.toLowerCase().includes('@all with access')) {
        comment.tags = 'everyone';
      } else if (tags.length) {
        comment.tags = {
          groups: tags.filter((tag) => !!groups.find((g) => g.id === tag)),
          users: tags.filter((tag) => !!users.find((g) => g.id === tag)),
        };
      }

      if (commentId) {
        comment.parentType = COMMENT_PARENT_TYPE.COMMENT;
        comment.parentId = commentId;
      } else if (submitTarget) {
        comment.parentType = submitTarget.parentType;
        comment.parentId = submitTarget.parentId;
      } else if (clipId) {
        comment.parentType = COMMENT_PARENT_TYPE.CLIP;
        comment.parentId = clipId;
      } else if (isTimeline && sortedSelectedClips.length && selectedClipsList?.length && section === COMMENT_PARENT_TYPE.CLIP) {
        comment.parentType = COMMENT_PARENT_TYPE.CLIP;
        if (window.location.pathname.toLowerCase().includes('composer')) {
          comment.parentId = composerRows[composerPlayControl.currentRowIndex].clips[composerPlayControl.currentClipIndex].id;
        } else if (currentPlayingTimelineClip) {
          comment.parentId = currentPlayingTimelineClip.id;
        }
      } else if (!isTimeline && selectedClipsList?.length) {
        comment.parentType = COMMENT_PARENT_TYPE.CLIP;
        if (window.location.pathname.toLowerCase().includes('composer')) {
          comment.parentId = composerRows[composerPlayControl.currentRowIndex].clips[composerPlayControl.currentClipIndex].id;
        } else if (currentPlayingTimelineClip) {
          comment.parentId = currentPlayingTimelineClip.id;
        }
      } else {
        comment.parentType = COMMENT_PARENT_TYPE.VIDEOFEED;
        comment.parentId = videofeedId as string;
      }

      if (commentData?.parent_type === COMMENT_PARENT_TYPE.COMMENT && commentData?.parent_id) {
        comment.parentType = COMMENT_PARENT_TYPE.COMMENT;
        comment.parentId = commentData?.parent_id;
      }

      if (comment?.parentType === COMMENT_PARENT_TYPE.CLIP && !comment?.parentId && selectedClipObject?.id) {
        comment.parentId = selectedClipObject.id;
      }
      const response = await createNewComment(comment);
      setMarkupValue('');
      setValue('');
      fetch(videofeedId as string);
      setTags([]);
      onPosted?.(response.data);
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage(NOTIFICATION.NOT_CREATED);
      }
    } finally {
      if (setShowFooter) {
        setShowFooter(false);
      }
      if (setImpertivlyShowFooter) {
        setImpertivlyShowFooter(false);
      }
      const hiddenHelper = document.querySelector(`#timelineFocusHelper`);
      if (hiddenHelper) {
        if (hiddenHelper) {
          hiddenHelper.setAttribute('tabIndex', '0');
          // @ts-ignore
          hiddenHelper.focus();
        }
      }
      setLoading(false);
    }
  };

  const onTagAdd = (id: string | number) => {
    if (`${id}` === NO_MENTION_TARGETS_ID || `${id}` === NO_RESULTS_ID) return;
    setTags((prevTags) => Array.from(new Set([...prevTags, `${id}`])));
  };

  useEffect(() => {
    return () => {
      setTags([]);
      if (mentionDebounceTimerRef.current) {
        clearTimeout(mentionDebounceTimerRef.current);
      }
    };
  }, []);

  const ref = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showFooter && footerRef?.current) {
      const footerPosition = footerRef.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: footerPosition + 500, // Adding 500px offset
        behavior: 'smooth',
      });
      focusOnInput(true);
    }
  }, [showFooter]);

  const focusOnInput = (moveCursorToEnd = false) => {
    const inputEl =
      ref.current ||
      (footerRef.current?.querySelector('textarea, input') as HTMLInputElement | HTMLTextAreaElement | null);
    if (inputEl) {
      inputEl.focus();
      if (moveCursorToEnd) {
        const valueLength = inputEl.value?.length || 0;
        inputEl.setSelectionRange?.(valueLength, valueLength);
      }
    }
  };

  const renderSuggestion = (suggestion: any, search: string, highlightedDisplay: React.ReactNode, index: number, focused: boolean) => {
    if (suggestion.id === ALL_WITH_ACCESS_ID) {
      return <div> All with access </div>;
    }
    if (suggestion.id === NO_MENTION_TARGETS_ID) {
      return <div style={{ opacity: 0.7, cursor: 'default', pointerEvents: 'none' }}>{NO_MENTION_TARGETS_LABEL}</div>;
    }
    if (suggestion.id === NO_RESULTS_ID) {
      return <div style={{ opacity: 0.7, cursor: 'default', pointerEvents: 'none' }}>{NO_RESULTS_LABEL}</div>;
    }
    return (
      <Tooltip
        title={
          <div>
            {' '}
            {suggestion?.title && (
              <>
                <div>{suggestion?.type === 'SUBGROUP' ? 'Type: Subgroup' : 'Type: Group'}</div>
                <div>
                  {!Number.isNaN(suggestion?.analysts?.length + suggestion?.viewers?.length) &&
                    `Users: ${suggestion?.analysts?.length + suggestion?.viewers?.length}`}
                </div>
                <div>{suggestion?.subgroups?.length > 0 ? `Subgroups: ${suggestion.subgroups.length}` : ''}</div>
              </>
            )}{' '}
          </div>
        }
      >
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <>
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {/* @ts-ignore */}
              {suggestion.display} {/* @ts-ignore */}
            </span>{' '}
          </>
        </div>
      </Tooltip>
    );
  };

  return (
    <div ref={footerRef} onClick={() => focusOnInput()}>
      <form name="scroll_here" onClick={() => focusOnInput()} onSubmit={handleSubmit(postNewComment)} className={cx(style, (clipId || target !== undefined) && styles.clipFooter)}>
        <Avatar className={styles.footerAvatar} avatar={currentUser?.avatar_url} />
        <MentionsInput
          placeholder={placeholder || (reply ? 'Send reply' : 'Send comment...')}
          aria-label={reply ? 'Reply' : 'Comment'}
          aria-describedby={target !== undefined ? 'sidebar-comment-target' : undefined}
          className={cx(styles.footerInput, reply && styles.smallReply, small && styles.customWidth)}
          value={markupValue}
          onChange={handleInputChange}
          onClick={handleInputClick}
          singleLine={false}
          style={mentionsStyle}
          allowSuggestionsAboveCursor={true}
          forceSuggestionsAboveCursor={true}
          suggestionsPortalHost={document.body}
          disabled={inputDisabled || loading}
          // @ts-ignore
          inputRef={ref}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              handleSubmit(postNewComment)();
            }
          }}
        >
          <Mention
            trigger="@"
            data={getUsersAndGroups}
            onAdd={onTagAdd}
            style={{ backgroundColor: '#1f1f1f', color: 'white', padding: '4px' }}
            displayTransform={(id, display) =>
              id === NO_MENTION_TARGETS_ID || id === NO_RESULTS_ID ? '' : `@${display} `
            }
            renderSuggestion={renderSuggestion}
          />
        </MentionsInput>

        <button onClick={() => focusOnInput()} type="submit" className={styles.footerButton} disabled={inputDisabled || loading}
          aria-label={reply ? 'Send reply' : 'Send comment'}>
          <ArrowUpIcon onClick={() => focusOnInput()} />
        </button>
      </form>
      {loading && (
        <>
          <Box sx={{ width: '100%' }}>
            <LinearProgress />
          </Box>
        </>
      )}
    </div>
  );
};

export default CommentFooter;
